import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";
import ModernRichTextEditor from "@/components/ModernRichTextEditor";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Platzhalter speziell für 2FA-E-Mails
const TFA_PLACEHOLDERS = [
  { key: "CODE", label: "CODE", description: "Der 6-stellige Verifikationscode" },
  { key: "NAME", label: "NAME", description: "Name des Benutzers" },
  { key: "GÜLTIGKEIT", label: "GÜLTIGKEIT", description: "Gültigkeitsdauer (10 Minuten)" },
];

export function TwoFactorEmailTemplate({ authToken }) {
  const { toast } = useToast();
  const editorRef = useRef(null);
  
  const [template, setTemplate] = useState({
    subject: "",
    html: ""
  });
  const [defaultTemplate, setDefaultTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [mailEditorColors, setMailEditorColors] = useState([
    "#000000", "#1f2937", "#1e40af", "#0f766e", "#b91c1c"
  ]);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` },
  });

  useEffect(() => {
    loadTemplate();
    loadEditorSettings();
    loadDefaultTemplate();
  }, [authToken]);

  const loadEditorSettings = async () => {
    if (!authToken) return;
    try {
      const res = await axios.get(`${API_BASE}/api/mail-editor-settings`, authHeaders());
      if (Array.isArray(res.data?.colors)) {
        setMailEditorColors(res.data.colors.slice(0, 5));
      }
    } catch (e) {
      console.error("Fehler beim Laden der Editor-Einstellungen:", e);
    }
  };

  const loadTemplate = async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/mail-templates/2fa`, authHeaders());
      setTemplate({
        subject: res.data.subject || "",
        html: res.data.html || ""
      });
    } catch (e) {
      console.error("Fehler beim Laden der 2FA-Vorlage:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultTemplate = async () => {
    if (!authToken) return;
    try {
      const res = await axios.get(`${API_BASE}/api/mail-templates/2fa/default`, authHeaders());
      setDefaultTemplate(res.data);
    } catch (e) {
      console.error("Fehler beim Laden der Standard-Vorlage:", e);
    }
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/mail-templates/2fa`, {
        subject: template.subject,
        html: template.html
      }, authHeaders());
      
      toast({ title: "2FA E-Mail-Vorlage gespeichert" });
    } catch (e) {
      console.error("Fehler beim Speichern:", e);
      toast({ title: "Fehler beim Speichern", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = async () => {
    setResetting(true);
    try {
      const res = await axios.post(`${API_BASE}/api/mail-templates/2fa/reset`, {}, authHeaders());
      
      setTemplate({
        subject: res.data.subject || "",
        html: res.data.html || ""
      });
      
      setShowResetConfirm(false);
      toast({ title: "Vorlage auf Standardwerte zurückgesetzt" });
    } catch (e) {
      console.error("Fehler beim Zurücksetzen:", e);
      toast({ title: "Fehler beim Zurücksetzen", variant: "destructive" });
    } finally {
      setResetting(false);
    }
  };

  const getPreviewHtml = () => {
    let html = template.html;
    // Platzhalter ersetzen für Vorschau
    html = html.replace(/\{\{CODE\}\}/g, '<span style="font-size: 32px; font-weight: bold; letter-spacing: 8px; color: #1e40af;">123456</span>');
    html = html.replace(/\{\{NAME\}\}/g, 'Max Mustermann');
    html = html.replace(/\{\{GÜLTIGKEIT\}\}/g, '10 Minuten');
    return html;
  };

  // Prüfen ob Vorlage von Standard abweicht
  const hasChanges = defaultTemplate && (
    template.subject !== defaultTemplate.subject || 
    template.html !== defaultTemplate.html
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="2fa-email-template">
      {/* Info-Box */}
      <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-800">
        <div className="flex items-start gap-3">
          <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium">Platzhalter für die E-Mail:</p>
            <ul className="mt-1 list-disc list-inside text-blue-700 space-y-0.5">
              <li><code className="bg-blue-100 px-1 rounded">{"{{CODE}}"}</code> – Der 6-stellige Verifikationscode</li>
              <li><code className="bg-blue-100 px-1 rounded">{"{{NAME}}"}</code> – Name des Benutzers</li>
              <li><code className="bg-blue-100 px-1 rounded">{"{{GÜLTIGKEIT}}"}</code> – Gültigkeitsdauer (10 Minuten)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Betreff */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">E-Mail-Betreff</label>
        <input
          type="text"
          value={template.subject}
          onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
          placeholder="Ihr Anmelde-Code für SFW Berlin"
          className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-slate-400 focus:outline-none"
          data-testid="2fa-template-subject"
        />
      </div>

      {/* Editor - ModernRichTextEditor wie im MailTemplateManager */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">E-Mail-Inhalt</label>
        <ModernRichTextEditor
          ref={editorRef}
          value={template.html}
          onChange={(html) => setTemplate(prev => ({ ...prev, html }))}
          placeholder="E-Mail-Text eingeben..."
          colors={mailEditorColors}
          placeholders={TFA_PLACEHOLDERS}
          showPlaceholders={true}
          minHeight="250px"
        />
      </div>

      {/* Aktionen */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
            data-testid="2fa-template-preview-btn"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {showPreview ? "Vorschau ausblenden" : "Vorschau anzeigen"}
          </button>
          
          {hasChanges && (
            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2 rounded-lg bg-amber-100 px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-200"
              data-testid="2fa-template-reset-btn"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Auf Standard zurücksetzen
            </button>
          )}
        </div>
        
        <button
          onClick={saveTemplate}
          disabled={saving}
          className="rounded-full bg-slate-900 px-6 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          data-testid="2fa-template-save-btn"
        >
          {saving ? "Speichern..." : "Vorlage speichern"}
        </button>
      </div>

      {/* Reset-Bestätigung */}
      {showResetConfirm && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
              <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-900">Auf Standardvorlage zurücksetzen?</h4>
              <p className="mt-1 text-xs text-amber-700">
                Ihre Änderungen an der 2FA E-Mail-Vorlage werden verworfen und durch die Standardvorlage ersetzt.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(false)}
                  className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={resetTemplate}
                  disabled={resetting}
                  className="rounded-full bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-500 disabled:opacity-50"
                  data-testid="2fa-template-reset-confirm-btn"
                >
                  {resetting ? "Zurücksetzen..." : "Ja, zurücksetzen"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vorschau */}
      {showPreview && (
        <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="2fa-template-preview">
          <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
            <p className="text-xs text-slate-500">Vorschau (mit Beispieldaten)</p>
          </div>
          <div className="bg-white p-6">
            <div className="mb-4 pb-4 border-b border-slate-100">
              <p className="text-sm text-slate-500">Betreff:</p>
              <p className="font-medium text-slate-900">{template.subject || "(Kein Betreff)"}</p>
            </div>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
            />
          </div>
        </div>
      )}

      {/* Status-Hinweis */}
      {!hasChanges && defaultTemplate && (
        <div className="flex items-center gap-2 text-xs text-emerald-600">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Vorlage entspricht der Standardversion
        </div>
      )}
    </div>
  );
}
